import { useMemo } from 'react';

import _round from 'lodash/round';

import {
  Column,
  ColumnGroup,
  TableHeader,
  Typography,
  Stack,
  LinkBehaviour,
} from '@cast/design-system';
import { Link } from '@mui/material';
import { WorkloadCostSummariesItem } from '@cast/types';

import { getRowRankClass } from 'components/table';
import { Link as RouterLink } from 'components/router';
import { TourStep } from 'core/tour';
import { CostChangePercentageCell } from 'features/cost-report/components/CostChangePercentageCell';
import { NamespacesLinkCell } from 'features/cost-report/components/NamespacesLinkCell';
import { NodePricingInfo } from 'features/cost-report/components/NodePricingInfo';
import { WorkloadDetailsLinkCell } from 'features/cost-report/components/WorkloadDetailsLinkCell';
import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';
import { formatDailyMetricsCost } from 'features/cost-report/utils/metrics/formatDailyMetricsCost';
import { TopMostExpensiveGroup } from 'types/top-items';

import { isStorageReportsSupported } from '../../../../../../utils';
import { tourSteps, workloadsTourId } from '../../tourSteps';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import {
  NoData,
  NoResults,
  SearchableTable,
  SearchableTableRecordsCount,
  useSearchContext,
} from '@cast/shared-search-ui';

export type WorkloadsTableProps = {
  cacheKey: string;
  selectedRows?: string[];
  onRowsSelected?: (ids: string[]) => void;
  topWorkloads?: TopMostExpensiveGroup[];
  getWorkloadLink?: (workload: WorkloadCostSummariesItem) => string;
  disableWorkloadLink?: boolean;
};

const round = (num?: string, precision = 2) =>
  num ? _round(Number(num), precision) : 0;

export const WorkloadsTable = (props: WorkloadsTableProps) => {
  const searchCtx = useSearchContext<WorkloadCostSummariesItem>();
  const { cluster } = useCluster();
  const showStorage = isStorageReportsSupported(cluster);
  const { hasGpuData, hasTpuData } = useCostReportContext();
  const workloads = searchCtx?.data;

  // Use same filtering approach as Footer
  const visibleSelectedCount = useMemo(() => {
    const selectedWorkloads = workloads?.filter(({ id }) =>
      props.selectedRows?.includes(id)
    );
    return selectedWorkloads?.length || 0;
  }, [props.selectedRows, workloads]);

  return (
    <SearchableTable
      cacheKey={props.cacheKey}
      testId="workload-compute-cost-table"
      maxHeight="80vh"
      selectedRows={props.selectedRows}
      onRowsSelected={props.onRowsSelected}
      components={{
        noData: <NoData entity="workload" />,
        noResults: <NoResults entity="workload" />,
      }}
      getRowClass={(row) =>
        getRowRankClass(props.topWorkloads?.findIndex((w) => w.id === row.id))
      }
      outerHeader={
        <TableHeader
          recordsCount={
            <SearchableTableRecordsCount
              title="workload"
              outOf={searchCtx?.metadata}
              count={visibleSelectedCount}
            />
          }
        />
      }
      sizing="l"
    >
      <Column<WorkloadCostSummariesItem>
        id="workloadName"
        header="Workload"
        minWidth={200}
        resizable
        renderCell={(workload, rowState) => {
          const { workloadName, namespace, workloadType } = workload;

          if (props.disableWorkloadLink) {
            const plainText = (
              <Typography variant="inherit" fontWeight={500} noWrap>
                {workloadName}
              </Typography>
            );

            if (rowState.index === 1) {
              return (
                <TourStep
                  step={tourSteps.nameCell}
                  type={workloadsTourId}
                  text="For an in-depth view, you can check this workload's costs over time by clicking here."
                  highlight="bubble"
                  placement="right"
                  beaconPosition={{
                    top: 0,
                    left: 40,
                  }}
                >
                  {plainText}
                </TourStep>
              );
            }

            return plainText;
          }

          if (props.getWorkloadLink) {
            const link = props.getWorkloadLink(workload);
            const inventoryLink = (
              <Link to={link} component={RouterLink} underline="none">
                <LinkBehaviour variant="inherit" fontWeight={500} noWrap>
                  {workloadName}
                </LinkBehaviour>
              </Link>
            );

            if (rowState.index === 1) {
              return (
                <TourStep
                  step={tourSteps.nameCell}
                  type={workloadsTourId}
                  text="For an in-depth view, you can check this workload's costs over time by clicking here."
                  highlight="bubble"
                  placement="right"
                  beaconPosition={{
                    top: 0,
                    left: 40,
                  }}
                >
                  {inventoryLink}
                </TourStep>
              );
            }

            return inventoryLink;
          }

          if (rowState.index === 1) {
            return (
              <TourStep
                step={tourSteps.nameCell}
                type={workloadsTourId}
                text="For an in-depth view, you can check this workload's costs over time by clicking here."
                highlight="bubble"
                placement="right"
                beaconPosition={{
                  top: 0,
                  left: 40,
                }}
              >
                <WorkloadDetailsLinkCell
                  workload={workloadName}
                  namespace={namespace}
                  workloadType={workloadType}
                />
              </TourStep>
            );
          }
          return (
            <WorkloadDetailsLinkCell
              workload={workloadName}
              namespace={namespace}
              workloadType={workloadType}
            />
          );
        }}
        sortable
      />
      <Column id="workloadType" header="Type" minWidth={120} sortable />
      <Column<WorkloadCostSummariesItem>
        id="namespace"
        header="Namespace"
        minWidth={140}
        renderCell={({ namespace }) => {
          return <NamespacesLinkCell namespace={namespace} />;
        }}
        sortable
      />
      <Column<WorkloadCostSummariesItem>
        id="podCount"
        header="Pods"
        minWidth={96}
        infoTooltip="Average hourly pods count"
        renderCell={({ cost }) => round(cost?.podCount, 0)}
        sortable
      />
      <ColumnGroup id="avg-req" title="AVG. REQ. PER HOUR">
        <Column<WorkloadCostSummariesItem>
          id="cpuCount"
          header="CPU"
          minWidth={100}
          infoTooltip="Average hourly CPU requests per workload"
          renderCell={({ cost }) => `${round(cost?.cpuCount)} CPU`}
          sortable
        />
        <Column<WorkloadCostSummariesItem>
          id="ramGib"
          header="Memory"
          minWidth={100}
          infoTooltip="Average hourly memory requests per workload"
          renderCell={({ cost }) => `${round(cost?.ramGib)} GiB`}
          sortable
        />
        {hasGpuData && (
          <Column<WorkloadCostSummariesItem>
            id="gpuCount"
            header="GPU"
            minWidth={100}
            infoTooltip="Average hourly GPU requests per workload"
            renderCell={({ cost }) => `${round(cost?.gpuCount)} GPU`}
            sortable
          />
        )}
        {hasTpuData && (
          <Column<WorkloadCostSummariesItem>
            id="tpuCount"
            header="TPU"
            minWidth={100}
            infoTooltip="Average hourly TPU requests per workload"
            renderCell={({ cost }) => `${round(cost?.tpuCount)} TPU`}
            sortable
          />
        )}
        {showStorage && (
          <Column<WorkloadCostSummariesItem>
            id="storageGib"
            header="Storage"
            minWidth={105}
            infoTooltip="Average hourly Storage requests per workload"
            renderCell={({ cost }) => `${round(cost?.storageGib)} GiB`}
            sortable
          />
        )}
      </ColumnGroup>
      <ColumnGroup id="total-cost" title="TOTAL COST PER RESOURCE">
        <Column<WorkloadCostSummariesItem>
          id="cpuCost"
          header="CPU"
          minWidth={104}
          renderCell={({ cost }) =>
            formatDailyMetricsCost(round(cost?.cpuCost))
          }
          sortable
        />
        <Column<WorkloadCostSummariesItem>
          id="ramCost"
          header="Memory"
          minWidth={104}
          renderCell={({ cost }) =>
            formatDailyMetricsCost(round(cost?.ramCost))
          }
          sortable
        />
        {hasGpuData && (
          <Column<WorkloadCostSummariesItem>
            id="gpuCost"
            header="GPU"
            minWidth={104}
            renderCell={({ cost }) =>
              formatDailyMetricsCost(round(cost?.gpuCost))
            }
            sortable
          />
        )}
        {hasTpuData && (
          <Column<WorkloadCostSummariesItem>
            id="tpuCost"
            header="TPU"
            minWidth={104}
            renderCell={({ cost }) =>
              formatDailyMetricsCost(round(cost?.tpuCost))
            }
            sortable
          />
        )}
        {showStorage && (
          <Column<WorkloadCostSummariesItem>
            id="storageCost"
            header="Storage"
            minWidth={105}
            renderCell={({ cost }) =>
              formatDailyMetricsCost(round(cost?.storageCost))
            }
            sortable
          />
        )}
      </ColumnGroup>
      <Column<WorkloadCostSummariesItem>
        id="totalCost"
        infoTooltip={
          showStorage ? (
            <>
              Sum of all pod costs in the workload.
              <br />
              <br />
              Storage cost is not split between resource offering types and
              added only to On-demand.
            </>
          ) : (
            'Sum of all pod costs in the workload'
          )
        }
        header="Total Cost"
        minWidth={152}
        renderCell={({ cost }) => {
          return (
            <Stack>
              <Typography variant="inherit" fontWeight="bold" noWrap>
                {formatDailyMetricsCost(Number(cost?.totalCost || 0))}
              </Typography>
              <NodePricingInfo
                onDemand={Number(cost?.costOnDemand || 0)}
                spot={Number(cost?.costSpot || 0)}
                fallback={Number(cost?.costSpotFallback || 0)}
                storage={Number(cost?.storageCost || 0)}
                showStorage={showStorage}
                hideTotal
                progressBarSx={{ height: '4px' }}
                sx={{ lineHeight: '4px' }}
              />
            </Stack>
          );
        }}
        sortable
      />
      <Column<WorkloadCostSummariesItem>
        id="totalCostPercentChange"
        infoTooltip="Shows the percentage difference in total cost compared to the previous period."
        header="Change"
        minWidth={130}
        sortable
        renderCell={({ cost }) => (
          <CostChangePercentageCell value={cost?.totalCostPercentChange} />
        )}
      />
    </SearchableTable>
  );
};
